import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Bell, BellRing, X, Check, AlertTriangle, MessageCircle, 
  Eye, Clock, Loader2, CheckCheck, Volume2, VolumeX
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Request notification permission
async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    return false;
  }
  
  if (Notification.permission === 'granted') {
    return true;
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  
  return false;
}

// Show browser notification
function showBrowserNotification(title, body, onClick) {
  if (Notification.permission === 'granted') {
    const notification = new Notification(title, {
      body,
      icon: '/logo192.png',
      badge: '/badge.png',
      vibrate: [200, 100, 200],
      requireInteraction: true
    });
    
    if (onClick) {
      notification.onclick = () => {
        window.focus();
        onClick();
        notification.close();
      };
    }
    
    // Auto close after 10 seconds
    setTimeout(() => notification.close(), 10000);
  }
}

// Play alert sound
function playAlertSound() {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 880;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    
    oscillator.start();
    
    setTimeout(() => {
      oscillator.frequency.value = 1100;
    }, 150);
    
    setTimeout(() => {
      oscillator.stop();
    }, 300);
  } catch (e) {
    if (process.env.NODE_ENV === 'development') console.log('Could not play alert sound');
  }
}

// Notification Item Component
function NotificationItem({ notification, onMarkRead, onAction }) {
  const severityConfig = {
    critical: { color: 'bg-red-100 border-red-300', icon: AlertTriangle, iconColor: 'text-red-600' },
    high: { color: 'bg-orange-100 border-orange-300', icon: AlertTriangle, iconColor: 'text-orange-600' },
    medium: { color: 'bg-yellow-100 border-yellow-300', icon: MessageCircle, iconColor: 'text-yellow-600' },
    low: { color: 'bg-blue-100 border-blue-300', icon: Bell, iconColor: 'text-blue-600' }
  };
  
  const severity = notification.data?.severity || 'low';
  const config = severityConfig[severity] || severityConfig.low;
  const Icon = config.icon;
  
  return (
    <div className={`p-3 rounded-lg border ${config.color} ${!notification.read ? 'shadow-sm' : 'opacity-75'}`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${notification.read ? 'bg-gray-100' : 'bg-white'}`}>
          <Icon className={`w-4 h-4 ${config.iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 text-sm">{notification.title}</p>
          <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{notification.body}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-gray-400">
              {new Date(notification.created_at).toLocaleTimeString()}
            </span>
            {notification.url && (
              <button 
                onClick={() => onAction(notification)}
                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
              >
                <Eye className="w-3 h-3" />
                View
              </button>
            )}
          </div>
        </div>
        {!notification.read && (
          <button 
            onClick={() => onMarkRead(notification.notification_id)}
            className="text-gray-400 hover:text-gray-600"
          >
            <Check className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// Main Notification Bell Component
export default function NotificationBell() {
  const { user, getAuthHeaders } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [lastCount, setLastCount] = useState(0);

  // Only show for admins/owners
  const isAdminOrOwner = user?.is_admin || user?.is_owner;

  const fetchNotifications = useCallback(async () => {
    if (!isAdminOrOwner) return;
    
    try {
      const response = await axios.get(`${API}/notifications/pending`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      const newNotifications = response.data?.notifications || [];
      
      // Check for new notifications
      if (newNotifications.length > lastCount && lastCount > 0) {
        const newest = newNotifications[0];
        
        // Play sound
        if (soundEnabled) {
          playAlertSound();
        }
        
        // Show browser notification
        if (permissionGranted && newest) {
          showBrowserNotification(
            newest.title,
            newest.body,
            () => {
              if (newest.url) {
                window.location.href = newest.url;
              }
            }
          );
        }
        
        // Show toast
        toast.warning(
          <div>
            <p className="font-semibold">{newest.title}</p>
            <p className="text-sm">{newest.body?.substring(0, 50)}...</p>
          </div>,
          { duration: 8000 }
        );
      }
      
      setNotifications(newNotifications);
      setLastCount(newNotifications.length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [getAuthHeaders, isAdminOrOwner, soundEnabled, permissionGranted, lastCount]);

  useEffect(() => {
    if (!isAdminOrOwner) return;
    
    // Request notification permission
    requestNotificationPermission().then(setPermissionGranted);
    
    // Initial fetch
    fetchNotifications();
    
    // Poll every 5 seconds for new notifications
    const interval = setInterval(fetchNotifications, 5000);
    
    return () => clearInterval(interval);
  }, [isAdminOrOwner, fetchNotifications]);

  const handleMarkRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/mark-read/${notificationId}`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setNotifications([]);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleAction = (notification) => {
    if (notification.url) {
      window.location.href = notification.url;
    }
    setShowDropdown(false);
  };

  const handleTestAlert = async () => {
    try {
      await axios.post(`${API}/notifications/test-alert`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Test alert sent!');
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to send test alert');
    }
  };

  if (!isAdminOrOwner) return null;

  const unreadCount = notifications.length;

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`relative p-2 rounded-full transition-colors ${
          unreadCount > 0 
            ? 'bg-red-100 text-red-600 hover:bg-red-200' 
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
        data-testid="notification-bell"
      >
        {unreadCount > 0 ? (
          <BellRing className="w-5 h-5 animate-pulse" />
        ) : (
          <Bell className="w-5 h-5" />
        )}
        
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold animate-bounce">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowDropdown(false)}
          />
          
          {/* Dropdown Content */}
          <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
            {/* Header */}
            <div className="p-3 bg-gray-50 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Notifications
                {unreadCount > 0 && (
                  <Badge className="bg-red-500 text-white">{unreadCount}</Badge>
                )}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className={`p-1.5 rounded-full ${soundEnabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-500'}`}
                  title={soundEnabled ? 'Sound on' : 'Sound off'}
                >
                  {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </button>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <CheckCheck className="w-3 h-3" />
                    Mark all read
                  </button>
                )}
              </div>
            </div>
            
            {/* Notifications List */}
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No new notifications</p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {notifications.map(notification => (
                    <NotificationItem
                      key={notification.notification_id}
                      notification={notification}
                      onMarkRead={handleMarkRead}
                      onAction={handleAction}
                    />
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-2 bg-gray-50 border-t">
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full text-xs"
                onClick={handleTestAlert}
              >
                <AlertTriangle className="w-3 h-3 mr-1" />
                Send Test Alert
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
